import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { PageContainer, Section, StatGrid } from '../../components/AdminContentWrapper';
import RequireRootRole from './components/RequireRootRole';
import ReconciliationSection from './components/ReconciliationSection';
import UsageAlertsSection from './components/UsageAlertsSection';
import CreditsEngineService from '../../../../services/CreditsEngineService';

const cellSx = { color: 'var(--wb-text)', borderColor: 'var(--wb-border)' };
const headSx = { ...cellSx, color: 'var(--wb-muted-85)', fontWeight: 600 };
const subCellSx = { ...cellSx, fontSize: '0.8rem', py: 0.75 };

const SERVICES = [
  { key: 'lambda', label: 'Lambda' },
  { key: 'apigw', label: 'API Gateway' },
  { key: 'dynamodb', label: 'DynamoDB' },
  { key: 's3', label: 'S3' },
  { key: 'transfer', label: 'Salida a internet' }
];

const SOURCE_LABELS = { lambda: 'Lambda', apigw: 'API Gateway', s3: 'S3' };

const isoDay = (date) => date.toISOString().slice(0, 10);

// Rangos predefinidos (UTC, igual que los agregados diarios del backend).
const rangeFor = (preset) => {
  const today = new Date();
  const end = isoDay(today);
  if (preset === 'month') return { start: `${end.slice(0, 7)}-01`, end };
  const days = { today: 0, '7d': 6, '30d': 29, '90d': 89 }[preset] ?? 6;
  const start = new Date(today);
  start.setUTCDate(start.getUTCDate() - days);
  return { start: isoDay(start), end };
};

// Montos chicos (fracciones de centavo): más decimales cuanto menor es.
const usd = (value) => {
  const n = Number(value) || 0;
  if (n === 0) return '$0';
  if (n < 0.01) return `$${n.toFixed(6)}`;
  if (n < 1) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(2)}`;
};

const num = (value) => (Number(value) || 0).toLocaleString('es-MX');

const bytes = (value) => {
  const n = Number(value) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
};

const shortAccount = (accountId) =>
  accountId === 'PLATFORM' ? 'Plataforma (sin tenant)' : accountId;

const ComponentsTable = ({ components }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell sx={headSx}>Componente</TableCell>
        <TableCell sx={headSx} align="right">Invocaciones / requests</TableCell>
        <TableCell sx={headSx} align="right">Errores</TableCell>
        <TableCell sx={headSx} align="right">Cold starts</TableCell>
        <TableCell sx={headSx} align="right">Duración prom.</TableCell>
        <TableCell sx={headSx} align="right">GB-s</TableCell>
        <TableCell sx={headSx} align="right">Entrada / salida</TableCell>
        <TableCell sx={headSx} align="right">DynamoDB (RU / WU)</TableCell>
        <TableCell sx={headSx} align="right">Costo</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {components.map((c) => (
        <TableRow key={`${c.source}#${c.component}`}>
          <TableCell sx={subCellSx}>
            <Typography component="span" sx={{ color: 'var(--wb-muted-80)', fontSize: '0.75rem', mr: 1 }}>
              {SOURCE_LABELS[c.source] || c.source}
            </Typography>
            {c.component}
          </TableCell>
          <TableCell sx={subCellSx} align="right">{num(c.count)}</TableCell>
          <TableCell sx={{ ...subCellSx, color: c.errors ? 'var(--wb-danger)' : subCellSx.color }} align="right">{num(c.errors)}</TableCell>
          <TableCell sx={subCellSx} align="right">{c.source === 'lambda' ? num(c.cold_starts) : '—'}</TableCell>
          <TableCell sx={subCellSx} align="right">{c.avg_duration_ms != null ? `${num(c.avg_duration_ms)} ms` : '—'}</TableCell>
          <TableCell sx={subCellSx} align="right">{c.source === 'lambda' ? c.gb_seconds : '—'}</TableCell>
          <TableCell sx={subCellSx} align="right">{bytes(c.bytes_in)} / {bytes(c.bytes_out)}</TableCell>
          <TableCell sx={subCellSx} align="right">
            {c.ddb_read_units || c.ddb_write_units ? `${c.ddb_read_units} / ${c.ddb_write_units}` : '—'}
          </TableCell>
          <TableCell sx={subCellSx} align="right">{usd(c.cost_usd)}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const TenantRow = ({ tenant, showAllocated }) => {
  const [open, setOpen] = useState(false);
  return (
    <Fragment>
      <TableRow hover sx={{ '&:hover': { backgroundColor: 'var(--wb-x-bg-0ea5e90f, rgba(14,165,233,0.06))' } }}>
        <TableCell sx={cellSx} padding="checkbox">
          <IconButton size="small" onClick={() => setOpen(!open)} sx={{ color: 'var(--wb-muted)' }}
            aria-label={open ? 'Ocultar componentes' : 'Ver componentes'}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ ...cellSx, fontFamily: 'monospace', fontSize: '0.8rem' }}>{shortAccount(tenant.account_id)}</TableCell>
        <TableCell sx={cellSx} align="right">{num(tenant.questions)}</TableCell>
        {SERVICES.map((s) => (
          <TableCell key={s.key} sx={cellSx} align="right">{usd(tenant.cost_usd?.[s.key])}</TableCell>
        ))}
        <TableCell sx={{ ...cellSx, fontWeight: 600 }} align="right">{usd(tenant.total_usd)}</TableCell>
        <TableCell sx={cellSx} align="right">{tenant.usd_per_question != null ? usd(tenant.usd_per_question) : '—'}</TableCell>
        {showAllocated && <TableCell sx={{ ...cellSx, color: 'var(--wb-warning)' }} align="right">{usd(tenant.allocated_usd)}</TableCell>}
      </TableRow>
      <TableRow>
        <TableCell sx={{ ...cellSx, py: 0 }} colSpan={SERVICES.length + (showAllocated ? 6 : 5)}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ my: 1.5, overflowX: 'auto' }}>
              <ComponentsTable components={tenant.components || []} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};

const InfrastructureCostsContent = () => {
  const [preset, setPreset] = useState('7d');
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Estable por preset: UsageAlertsSection recarga cuando cambia.
  const range = useMemo(() => rangeFor(preset), [preset]);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      setReport(await CreditsEngineService.getInfrastructureUsage(range));
    } catch (err) {
      setError(err.message || 'No fue posible obtener los costos de infraestructura.');
    } finally {
      setIsLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const tenants = report?.tenants || [];
  const totalQuestions = tenants.reduce((sum, t) => sum + (t.questions || 0), 0);
  const showAllocated = Boolean(report?.reconciliation);

  return (
    <PageContainer
      title="Costos de infraestructura"
      description="Costo INTERNO estimado de AWS por tenant (Lambda, API Gateway, DynamoDB, S3 y salida de datos), calculado con precios de lista sobre el uso medido. No son créditos ni lo que paga el tenant."
      actions={
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={isLoading}
          sx={{ borderColor: 'var(--wb-blue-40)', color: 'var(--wb-blue)', '&:hover': { borderColor: 'var(--wb-blue)' } }}>
          Actualizar
        </Button>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error al cargar datos</AlertTitle>
          {error}
        </Alert>
      )}

      <Section title="Período">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel sx={{ color: 'var(--wb-text-soft)' }}>Rango</InputLabel>
            <Select value={preset} label="Rango" onChange={(e) => setPreset(e.target.value)} sx={{ color: 'var(--wb-text-strong)' }}>
              <MenuItem value="today">Hoy</MenuItem>
              <MenuItem value="7d">Últimos 7 días</MenuItem>
              <MenuItem value="month">Mes en curso</MenuItem>
              <MenuItem value="30d">Últimos 30 días</MenuItem>
              <MenuItem value="90d">Últimos 90 días</MenuItem>
            </Select>
          </FormControl>
          {report && (
            <Typography sx={{ color: 'var(--wb-muted-80)' }}>
              {report.start} a {report.end} (UTC)
            </Typography>
          )}
        </Stack>
      </Section>

      {isLoading && !report ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} sx={{ color: 'var(--wb-primary)' }} />
        </Box>
      ) : report && (
        <>
          <Box sx={{ mb: 3 }}>
            <StatGrid items={[
              { label: 'Costo total estimado', value: usd(report.total_usd) },
              { label: 'Tenants con consumo', value: num(tenants.filter((t) => t.account_id !== 'PLATFORM').length) },
              { label: 'Preguntas al agente', value: num(totalQuestions) },
              { label: 'Costo promedio por pregunta', value: totalQuestions ? usd(report.total_usd / totalQuestions) : '—' }
            ]} />
          </Box>

          <Section
            title="Costo por tenant"
            subtitle="Despliega un tenant para ver cada Lambda y API. Los agregados se actualizan cada ~1 minuto; los de S3, cuando AWS entrega sus access logs (puede tardar horas). Para cifras exactas sin duplicados usa la consulta guardada de Athena «costo-por-tenant»."
          >
            {tenants.length === 0 ? (
              <Typography sx={{ color: 'var(--wb-muted-70)', py: 3, textAlign: 'center' }}>
                No hay consumo registrado para el período seleccionado.
              </Typography>
            ) : (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headSx} padding="checkbox" />
                      <TableCell sx={headSx}>Tenant (account_id)</TableCell>
                      <TableCell sx={headSx} align="right">Preguntas</TableCell>
                      {SERVICES.map((s) => (
                        <TableCell key={s.key} sx={headSx} align="right">{s.label}</TableCell>
                      ))}
                      <TableCell sx={headSx} align="right">Total</TableCell>
                      <TableCell sx={headSx} align="right">
                        <Tooltip title="Costo total del tenant dividido entre sus preguntas al agente (incluye el resto de su actividad en la plataforma).">
                          <span>Por pregunta</span>
                        </Tooltip>
                      </TableCell>
                      {showAllocated && (
                        <TableCell sx={headSx} align="right">
                          <Tooltip title="Costo REAL total de la cuenta de AWS (Cost Explorer, incluye servicios no medidos por request) repartido en proporción al costo estimado de cada tenant.">
                            <span>Asignado (real)</span>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tenants.map((tenant) => (
                      <TenantRow key={tenant.account_id} tenant={tenant} showAllocated={showAllocated} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Section>

          <ReconciliationSection reconciliation={report.reconciliation} usd={usd} />
        </>
      )}

      <UsageAlertsSection range={range} />
    </PageContainer>
  );
};

const InfrastructureCosts = () => (
  <RequireRootRole title="Costos de infraestructura">
    <InfrastructureCostsContent />
  </RequireRootRole>
);

export default InfrastructureCosts;
